import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import ImportModal from '../components/ImportModal';
import { useCompany } from '../context/CompanyContext';
import api from '../api';
import './Dashboard.css';

const CLIENT_TYPES = ['Individual', 'Company', 'LLP', 'Partnership', 'Trust'];
const SERVICES = [
  'CFO Services',
  'ERP Implementation',
  'Audit',
  'Taxation Compliance',
  'ITR Filing',
  'Bookkeeping',
  'Financial Due Diligence',
  'Valuation',
  'MIS/Dashboard',
];

const EMPTY_FORM = {
  name_of_client: '',
  type_of_client: 'Company',
  nature_of_service: [],
  otherChecked: false,
  otherService: '',
  concern_person: '',
  mobile: '',
  email: '',
  remarks: '',
};

function ClientMaster() {
  const { selectedCompanyId } = useCompany();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const refresh = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get('/clients', { params: { company_id: selectedCompanyId } }).then((res) => setClients(res.data));
  };

  useEffect(() => {
    setLoading(true);
    setSelectedIds([]);
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const allSelected = clients.length > 0 && selectedIds.length === clients.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : clients.map((c) => c.id));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openBulkDelete = () => {
    if (selectedIds.length === 0 || !selectedCompanyId) return;
    setBulkDeleteOpen(true);
    setBulkResult(null);
    setBulkPreview(null);
    setBulkPreviewLoading(true);
    api
      .post('/clients/bulk-delete-preview', { company_id: selectedCompanyId, client_ids: selectedIds })
      .then((res) => setBulkPreview(res.data))
      .finally(() => setBulkPreviewLoading(false));
  };

  const confirmBulkDelete = () => {
    if (selectedIds.length === 0 || !selectedCompanyId) return;
    setBulkDeleting(true);
    api
      .post('/clients/bulk-delete', { company_id: selectedCompanyId, client_ids: selectedIds })
      .then((res) => {
        setBulkResult(res.data);
        setSelectedIds([]);
        refresh();
      })
      .finally(() => setBulkDeleting(false));
  };

  const closeBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkPreview(null);
    setBulkResult(null);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    const existing = c.nature_of_service ? c.nature_of_service.split(',').map((s) => s.trim()).filter(Boolean) : [];
    const known = existing.filter((s) => SERVICES.includes(s));
    const unknown = existing.filter((s) => !SERVICES.includes(s));
    setForm({
      name_of_client: c.name_of_client || '',
      type_of_client: c.type_of_client || 'Company',
      nature_of_service: known,
      otherChecked: unknown.length > 0,
      otherService: unknown.join(', '),
      concern_person: c.concern_person || '',
      mobile: c.mobile || '',
      email: c.email || '',
      remarks: c.remarks || '',
    });
    setModalOpen(true);
  };

  const toggleService = (service) => {
    setForm((f) => ({
      ...f,
      nature_of_service: f.nature_of_service.includes(service)
        ? f.nature_of_service.filter((s) => s !== service)
        : [...f.nature_of_service, service],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) return;
    setSaving(true);
    const allServices = [...form.nature_of_service];
    if (form.otherChecked && form.otherService.trim()) {
      allServices.push(form.otherService.trim());
    }
    const { otherChecked, otherService, ...rest } = form;
    const payload = { ...rest, nature_of_service: allServices.join(', '), company_id: selectedCompanyId };
    const request = editingId ? api.put(`/clients/${editingId}`, payload) : api.post('/clients', payload);
    request
      .then(() => {
        refresh();
        showToast(editingId ? 'Client updated ✓' : 'Client added ✓');
        setModalOpen(false);
      })
      .finally(() => setSaving(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    api.delete(`/clients/${deleteTarget.id}`).then(() => {
      refresh();
      showToast(`${deleteTarget.name_of_client} deleted`);
      setDeleteTarget(null);
    });
  };

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">Client Master</h1>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}

      <div className="dash-card">
        <div className="page-title-row">
          <h2 className="dash-card-title">Your Clients</h2>
          <div className="table-actions">
            {selectedIds.length > 0 && (
              <button
                className="dash-btn-red-outline"
                type="button"
                onClick={openBulkDelete}
              >
                Delete Selected ({selectedIds.length})
              </button>
            )}
            <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
              Import
            </button>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAdd}>
              + Add New Client
            </button>
          </div>
        </div>

        <table className="dash-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected)}
                  onChange={toggleSelectAll}
                  aria-label="Select all clients"
                />
              </th>
              <th>Client Name</th>
              <th>Type</th>
              <th>Nature of Service</th>
              <th>Concern Person</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && clients.length === 0 && (
              <tr>
                <td className="dash-empty" colSpan={9}>No clients yet. Add your first one.</td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(c.id)}
                    onChange={() => toggleSelectOne(c.id)}
                    aria-label={`Select ${c.name_of_client}`}
                  />
                </td>
                <td>{c.name_of_client}</td>
                <td>{c.type_of_client || '—'}</td>
                <td>{c.nature_of_service || '—'}</td>
                <td>{c.concern_person || '—'}</td>
                <td>{c.mobile || '—'}</td>
                <td>{c.email || '—'}</td>
                <td className="remarks-cell" title={c.remarks || ''}>{c.remarks || '—'}</td>
                <td>
                  <div className="table-actions">
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEdit(c)}>
                      Edit
                    </button>
                    <button className="dash-btn-red-outline" type="button" onClick={() => setDeleteTarget(c)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="ncf-modal company-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingId ? 'Edit Client' : 'Add New Client'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="field-group">
                <span className="field-label">Name of Client *</span>
                <input
                  className="field-input"
                  required
                  value={form.name_of_client}
                  onChange={(e) => setForm({ ...form, name_of_client: e.target.value })}
                />
              </div>

              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Type of Client</span>
                  <select
                    className="field-select"
                    value={form.type_of_client}
                    onChange={(e) => setForm({ ...form, type_of_client: e.target.value })}
                  >
                    {CLIENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <span className="field-label">Concern Person</span>
                  <input
                    className="field-input"
                    value={form.concern_person}
                    onChange={(e) => setForm({ ...form, concern_person: e.target.value })}
                  />
                </div>
              </div>

              <div className="field-group">
                <span className="field-label">Nature of Service</span>
                <div className="checkbox-list">
                  {SERVICES.map((s) => (
                    <label key={s} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={form.nature_of_service.includes(s)}
                        onChange={() => toggleService(s)}
                      />
                      {s}
                    </label>
                  ))}
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={form.otherChecked}
                      onChange={() => setForm((f) => ({ ...f, otherChecked: !f.otherChecked }))}
                    />
                    Other
                  </label>
                </div>
                {form.otherChecked && (
                  <input
                    className="field-input"
                    style={{ marginTop: 8 }}
                    placeholder="Specify other service(s), comma-separated"
                    value={form.otherService}
                    onChange={(e) => setForm({ ...form, otherService: e.target.value })}
                  />
                )}
              </div>

              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Mobile</span>
                  <input
                    className="field-input"
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">Email</span>
                  <input
                    className="field-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="field-group">
                <span className="field-label">Remarks</span>
                <input
                  className="field-input"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                />
              </div>

              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Client</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete <strong>{deleteTarget.name_of_client}</strong>? This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkDeleteOpen && (
        <div className="ncf-modal-overlay" onClick={() => !bulkDeleting && closeBulkDelete()}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            {!bulkResult ? (
              <>
                <h3 className="ncf-modal-title">Delete {selectedIds.length} Client{selectedIds.length === 1 ? '' : 's'}?</h3>
                {bulkPreviewLoading && (
                  <p style={{ fontSize: 14, color: '#6b7280' }}>Checking for linked records...</p>
                )}
                {!bulkPreviewLoading && bulkPreview && (
                  <>
                    <p style={{ fontSize: 14, color: '#374151' }}>
                      <strong>{bulkPreview.deleted_ids.length}</strong> client(s) will be permanently deleted. This cannot be undone.
                    </p>
                    {bulkPreview.skipped.length > 0 && (
                      <>
                        <p style={{ fontSize: 14, color: '#b45309', marginTop: 8 }}>
                          <strong>{bulkPreview.skipped.length}</strong> selected client(s) have linked compliance records and will be <strong>skipped</strong> (not deleted) to avoid losing that data:
                        </p>
                        <ul style={{ fontSize: 13, color: '#6b7280', maxHeight: 160, overflowY: 'auto' }}>
                          {bulkPreview.skipped.map((s) => (
                            <li key={s.id}>{s.name} — {s.reason}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {bulkPreview.deleted_ids.length === 0 && (
                      <p style={{ fontSize: 14, color: '#b45309' }}>
                        No clients can be deleted — all selected clients have linked records. Unlink or delete those first.
                      </p>
                    )}
                  </>
                )}
                <div className="ncf-modal-actions">
                  <button className="dash-btn cal-btn-grey" type="button" onClick={closeBulkDelete} disabled={bulkDeleting}>
                    Cancel
                  </button>
                  <button
                    className="dash-btn dash-btn-orange"
                    style={{ background: '#dc2626' }}
                    type="button"
                    onClick={confirmBulkDelete}
                    disabled={bulkDeleting || bulkPreviewLoading || !bulkPreview || bulkPreview.deleted_ids.length === 0}
                  >
                    {bulkDeleting ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="ncf-modal-title">Delete Results</h3>
                <p style={{ fontSize: 14, color: '#374151' }}>
                  <strong>{bulkResult.deleted_count}</strong> client(s) deleted.
                </p>
                {bulkResult.skipped.length > 0 && (
                  <>
                    <p style={{ fontSize: 14, color: '#374151', marginTop: 8 }}>
                      <strong>{bulkResult.skipped.length}</strong> skipped (linked records exist):
                    </p>
                    <ul style={{ fontSize: 13, color: '#6b7280', maxHeight: 180, overflowY: 'auto' }}>
                      {bulkResult.skipped.map((s) => (
                        <li key={s.id}>{s.name} — {s.reason}</li>
                      ))}
                    </ul>
                  </>
                )}
                <div className="ncf-modal-actions">
                  <button className="dash-btn dash-btn-orange" type="button" onClick={closeBulkDelete}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="client"
        moduleLabel="Client Master"
        companyId={selectedCompanyId}
        onImported={refresh}
        supportsCreateNew
      />
    </PageLayout>
  );
}

export default ClientMaster;
